// Small shared visual pieces: rainbow text, the film date stamp, icons, labels.
import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { Link } from 'react-router';
import { RAINBOW, colorFor, segments } from '../lib/text';
import { yearColor, yearFontStyle } from '../lib/years';
import { useDB } from '../data/store';

/** Rainbow text, one solid colour per letter like old toy and TV logos (no gradients). */
export function Rainbow({ text, offset = 0, className }: { text: string; offset?: number; className?: string }) {
  let i = offset;
  return (
    <span className={className}>
      <span className="sr">{text}</span>
      <span aria-hidden="true">{Array.from(text).map((ch, k) => ch.trim() === ''
        ? <span key={k}>{ch}</span>
        : <span key={k} style={{ color: `var(--rb-${RAINBOW[i++ % RAINBOW.length]})` }}>{ch}</span>)}</span>
    </span>
  );
}

const SEG: Record<string, string> = { a: '1.4,1 2.4,0 7.6,0 8.6,1 7.6,2 2.4,2', b: '9,1.4 10,2.4 10,7.6 9,8.6 8,7.6 8,2.4', c: '9,9.4 10,10.4 10,15.6 9,16.6 8,15.6 8,10.4', d: '1.4,17 2.4,16 7.6,16 8.6,17 7.6,18 2.4,18', e: '1,9.4 2,10.4 2,15.6 1,16.6 0,15.6 0,10.4', f: '1,1.4 2,2.4 2,7.6 1,8.6 0,7.6 0,2.4', g: '1.4,9 2.4,8 7.6,8 8.6,9 7.6,10 2.4,10' };
const DIG: Record<string, string> = { 0: 'abcdef', 1: 'bc', 2: 'abged', 3: 'abgcd', 4: 'fgbc', 5: 'afgcd', 6: 'afgedc', 7: 'abc', 8: 'abcdefg', 9: 'abcdfg' };
/** The orange date stamp old film cameras burned into photos, drawn as seven-segment digits. */
export function Stamp({ text, label, className, style }: { text: string; label: string; className?: string; style?: CSSProperties }) {
  let x = 0;
  const parts: ReactNode[] = [];
  for (const ch of text) {
    const segs = DIG[ch];
    if (segs) { for (const s of segs) parts.push(<polygon key={`${x}${s}`} points={SEG[s]!.split(' ').map(p => { const [a, b] = p.split(','); return `${Number(a) + x},${b}`; }).join(' ')} />); x += 13; }
    else if (ch === "'") { parts.push(<rect key={`q${x}`} x={x + 1} y={0} width={2} height={5.5} />); x += 6; }
    else if (ch === ' ') x += 6;
  }
  return (
    <svg className={'seg7 ' + (className ?? '')} style={style} viewBox={`-3 0 ${Math.max(x - 3, 1) + 4} 18`} role="img" aria-label={label}>
      <g transform="skewX(-8) translate(2.5 0)">{parts}</g>
    </svg>
  );
}

/** A year written in its own era font and rainbow colour, linking to its year page. */
export function YearLink({ year, className }: { year: number; className?: string }) {
  const { settings } = useDB();
  return (
    <Link to={`/y/${year}`} className={'yearlink ' + (className ?? '')} style={{ color: yearColor(year), ...(settings.yearFonts ? yearFontStyle(year) : {}) }}>
      {year}
    </Link>
  );
}

export function TagLink({ tag }: { tag: string }) {
  return <Link to={`/t/${encodeURIComponent(tag)}`} className="taglink" style={{ color: colorFor(tag) }}>#{tag}</Link>;
}

/** Text with #tags and @mentions turned into links. User text is never inserted as HTML. */
export function RichText({ text }: { text: string }) {
  return <>{segments(text).map((s, i) => s.kind === 'tag' ? <TagLink key={i} tag={s.tag} />
    : s.kind === 'mention' ? <Link key={i} to={`/u/${s.handle}`} className="mention">{s.value}</Link>
    : <span key={i}>{s.value}</span>)}</>;
}

export function ExampleLabel() { return <span className="example-label" title="Made-up example content for the preview">Example</span>; }

const ICONS: Record<string, ReactNode> = {
  ghost: <><path className="fill-on" d="M12 3a7 7 0 0 0-7 7v11l2.5-2 2.5 2 2-2 2 2 2.5-2 2.5 2V10a7 7 0 0 0-7-7z" /><circle className="eye" cx="9.5" cy="10.5" r="1.1" /><circle className="eye" cx="14.5" cy="10.5" r="1.1" /></>,
  heart: <path className="fill-on" d="M12 20 4.6 12.6a4.4 4.4 0 0 1 6.2-6.2L12 7.6l1.2-1.2a4.4 4.4 0 0 1 6.2 6.2z" />,
  pass: <path d="M4 8h13M14 5l3 3-3 3M20 16H7M10 13l-3 3 3 3" />,
  comment: <path d="M4 5h16v11H9.5L4 20z" />,
  save: <path className="fill-on" d="M6 3h12v18l-6-4.5L6 21z" />,
  more: <><circle cx="5" cy="12" r="1.2" /><circle cx="12" cy="12" r="1.2" /><circle cx="19" cy="12" r="1.2" /></>,
  rewind: <path d="M11 6 4 12l7 6zM20 6l-7 6 7 6z" />,
  search: <><circle cx="10.5" cy="10.5" r="6.5" /><path d="m15.5 15.5 5 5" /></>,
  sun: <><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" /></>,
  moon: <path d="M20 14.5A8 8 0 1 1 9.5 4a6.5 6.5 0 0 0 10.5 10.5z" />,
  bell: <path d="M6 16v-5a6 6 0 0 1 12 0v5l2 2H4zM10 21h4" />,
  plus: <path d="M12 5v14M5 12h14" />,
  user: <><circle cx="12" cy="8" r="4" /><path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8" /></>,
  share: <path d="M12 3v12M7 8l5-5 5 5M5 13v7h14v-7" />,
  flag: <path d="M5 21V4h12l-2.5 4 2.5 4H5" />,
  know: <path d="M9 18h6M10 21h4M12 3a6 6 0 0 0-3.6 10.8c.6.5 1.1 1.3 1.1 2.2h5c0-.9.5-1.7 1.1-2.2A6 6 0 0 0 12 3z" />,
  dice: <><rect x="4" y="4" width="16" height="16" rx="3" /><circle cx="9" cy="9" r="1.2" /><circle cx="15" cy="15" r="1.2" /><circle cx="15" cy="9" r="1.2" /><circle cx="9" cy="15" r="1.2" /></>,
  menu: <path d="M4 7h16M4 12h16M4 17h16" />,
  x: <path d="M6 6l12 12M18 6 6 18" />,
};
export function Icon({ name, className }: { name: keyof typeof ICONS | string; className?: string }) {
  return <svg className={'ic ' + (className ?? '')} viewBox="0 0 24 24" aria-hidden="true">{ICONS[name]}</svg>;
}

export function Avatar({ name, color }: { name: string; color: string }) {
  return <span className="av" aria-hidden="true" style={{ color: `var(--rb-${color})`, borderColor: `var(--rb-${color})` }}>{name.charAt(0).toUpperCase()}</span>;
}

export function timeAgo(t: number, now = Date.now()): string {
  const m = Math.floor((now - t) / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.floor(h / 24);
  return d === 1 ? '1 day ago' : `${d} days ago`;
}

/** Two taps instead of a browser confirm() pop-up (which some embedded views block). */
export function ConfirmButton({ label, confirmLabel, onConfirm, className, role }: { label: string; confirmLabel: string; onConfirm: () => void; className?: string; role?: string }) {
  const [armed, setArmed] = useState(false);
  useEffect(() => { if (!armed) return; const t = window.setTimeout(() => setArmed(false), 4000); return () => window.clearTimeout(t); }, [armed]);
  return <button type="button" role={role} className={className + (armed ? ' armed' : '')} onClick={e => { e.stopPropagation(); if (armed) { setArmed(false); onConfirm(); } else setArmed(true); }}>{armed ? confirmLabel : label}</button>;
}
